import { CalendarMonth, ChevronLeft, ChevronRight } from '@mui/icons-material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { ButtonIcons } from '@/components/ButtonIcons';
import { mapViewTableToText, ViewTable } from '@/configs/ViewTable';
import { useYaMetrika } from '@/entities/yaMetrika';
import { DateNavigationModel } from '@/models/DateNavigationModel';

import { Calendar } from './Calendar';
import { AnalyticsEvent } from '@/types/yametrika';

import s from './TimesheetControls.module.scss';

type DatePickerProps = {
  dateNavigation: DateNavigationModel;
  allowedViews?: ViewTable[];
  children?: React.ReactNode;
};

const DEFAULT_VIEWS = [ViewTable.Week, ViewTable.Month];

const DatePicker: React.FC<DatePickerProps> = observer(
  ({ dateNavigation, allowedViews = DEFAULT_VIEWS, children }) => {
    const { sendEvent } = useYaMetrika();
    const { prevPeriod, nextPeriod, setView, view, title, goToToday, isCurrentPeriod } =
      dateNavigation;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    useHotkeys('arrowleft', (e) => {
      e.preventDefault();
      prevPeriod();
      sendEvent(AnalyticsEvent.useHotkeys);
    });

    useHotkeys('arrowright', (e) => {
      e.preventDefault();
      nextPeriod();
      sendEvent(AnalyticsEvent.useHotkeys);
    });

    useHotkeys('arrowdown', (e) => {
      e.preventDefault();
      setView(view);
      sendEvent(AnalyticsEvent.useHotkeys);
    });

    useHotkeys('escape', () => {
      if (anchorEl) {
        setAnchorEl(null);
        sendEvent(AnalyticsEvent.useHotkeys);
      }
    });

    const onCalendarClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(anchorEl ? null : e.currentTarget);
      sendEvent(AnalyticsEvent.clickMiniCalendar);
    };

    const onViewClick = (viewItem: ViewTable) => {
      const event =
        viewItem === ViewTable.Month ? AnalyticsEvent.switchMonth : AnalyticsEvent.switchWeek;
      sendEvent(event);

      setView(viewItem);
    };

    return (
      <div className={s.date_picker}>
        <div className={s.date_picker__title}>
          <ButtonIcons onClick={onCalendarClick}>
            <CalendarMonth />
          </ButtonIcons>
          <span>{title}</span>
        </div>

        <Calendar
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          dateNavigation={dateNavigation}
        />

        <div className={s.date_picker__viewer}>
          <div
            onClick={isCurrentPeriod ? undefined : goToToday}
            className={clsx(s['date_picker__viewer-pick'], {
              [s['date_picker__viewer-pick_disabled']]: isCurrentPeriod,
            })}
          >
            Сегодня
          </div>
          <div className={s['date_picker__viewer-separator']} />
          <ButtonIcons onClick={prevPeriod}>
            <ChevronLeft />
          </ButtonIcons>
          <ButtonIcons onClick={nextPeriod}>
            <ChevronRight />
          </ButtonIcons>
          {allowedViews.length > 1 &&
            allowedViews.map((viewItem) => (
              <div
                onClick={() => onViewClick(viewItem)}
                key={viewItem}
                className={clsx(s['date_picker__viewer-pick'], {
                  [s.activate]: viewItem === view,
                })}
              >
                {mapViewTableToText[viewItem]}
              </div>
            ))}
          {children}
        </div>
      </div>
    );
  },
);

export { DatePicker };
